import { DataTable } from "../../components/DataTable.tsx";
import { Button } from "../../components/ui/button.tsx";
import { FaDownload, FaEye, FaPencilAlt, FaFilter } from "react-icons/fa";
import Modal from "../../components/Modal.tsx";
import { Label } from "../../components/ui/label.tsx";
import React, { useEffect, useMemo, useState } from "react";
import ShortenedText from "../../components/ShortenedText";
import LoadingImage from "../../components/LoadingImage.js";
import { Input } from "../../components/ui/input.tsx";
import { TextArea } from "../../components/ui/TextArea.tsx";
import { createItemStore } from "../../stores/createItemStore";
import { transactionDataMap, transactionService } from "../../services";
import useMaterialStore from "../../stores/materialStore";
import MaterialSelectTransactions from "../../components/MaterialSelectTransactions.js";
// Date picker imports
import { DateRange } from "react-date-range";
import { format } from "date-fns";
import "react-date-range/dist/styles.css"; // main style file
import "react-date-range/dist/theme/default.css"; // theme css file
import { toast } from "react-toastify";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select.tsx";
import StatusBadge from "../../components/table/StatusBadge"

const exportToCsv = (data, filename) => {
  if (!data || data.length === 0) return;

  // Define columns to exclude
  const excludedColumns = ["isCollector", "materialId", "reason", "date"];

  // Prepare CSV content with mapped headers and filtered columns
  const headers = Object.keys(data[0])
    .filter((key) => !excludedColumns.includes(key)) // Filter out unwanted columns
    .map((key) => csvHeaderMap[key] || key) // Use mapped header if available
    .join(",");

  const rows = data
    .map((row) =>
      Object.entries(row)
        .filter(([key]) => !excludedColumns.includes(key)) // Filter out unwanted columns
        .map(([, value]) =>
          typeof value === "string" ? `"${value.replace(/"/g, '""')}"` : value
        )
        .join(",")
    )
    .join("\n");

  const csvContent = `${headers}\n${rows}`;

  // Create download link
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const columns = [
  {
    accessorKey: "dateCreated",
    header: "Date Created",
    cell: ({ row }) => (
      <span>
        {new Date(row.getValue("dateCreated")).toLocaleString().split(",")[0]}
      </span>
    ),
  },
  {
    accessorKey: "pocaName",
    header: () => (
      <div className="space-y-1">
        <div className="flex items-center">
          <span className="inline-block w-2 h-2 rounded-full bg-red-500 mr-1.5"></span>
          <span>POCA</span>
        </div>
        <div className="flex items-center">
          <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-1.5"></span>
          <span>Collector</span>
        </div>
      </div>
    ),
    cell: ({ row }) => {
      const isCollector = row.getValue("pocaName") == null;
      const value = row.getValue("pocaName");

      return (
        <div className="flex items-center">
          <span
            className={`inline-block w-2 h-2 rounded-full mr-1.5 ${
              isCollector ? "bg-green-500" : "bg-red-500"
            }`}
          ></span>
          <span>{value ?? row.original.collectorName}</span>
        </div>
      );
    },
  },
  {
    accessorKey: "profileName",
    header: "Profile Name",
    cell: ({ row }) => <span>{row.getValue("profileName")}</span>,
  },
  {
    accessorKey: "pocName",
    header: "POC Name",
    cell: ({ row }) => <span>{row.getValue("pocName")}</span>,
  },
  {
    accessorKey: "materialCategory",
    header: "Material Category",
    cell: ({ row }) => <span>{row.getValue("materialCategory")}</span>,
  },
  {
    accessorKey: "quantity",
    header: "Quantity",
    cell: ({ row }) => (
      <span>
        {row.getValue("quantity")} {row.original.materialUnit}
      </span>
    ),
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => {
      const status = row.getValue("status");
      return (
        <StatusBadge status={status} />
      );
    },
  },
];

const csvHeaderMap = {
  dateCreated: "Date Created",
  POCA: "POC Attendant/Collector",
  pocAttendantName: "POC Attendant Name",
  collectorName: "Collector Name",
  profileName: "Profile Name",
  pocName: "POC Name",
  materialCategory: "Material Category",
  materialUnit: "Material Unit",
  quantity: "Quantity",
  status: "Status",
  profId: "Profile ID",
  pocId: "POC ID",
  transactionId: "Transaction ID",
  dateUpdated: "Date Updated",
  // Removed: isCollector, materialId, reason, date
};

// ========================================================
// Step 1: Zustand store instantiation
// ========================================================
// const transactionDataMap = (item) => {
//   return {
//     transactionId: item?._id ?? "N/A",
//     pocId: item?.poc?._id ?? "N/A",
//     pocName: item?.poc?.name ?? "N/A",
//     POCA: item?.poc_attendant?.name ?? item?.collector?.name ?? "N/A",
//     isCollector: !!item?.collector?.name,
//     pocAttendantName: item?.poc_attendant?.name,
//     collectorName: item?.collector?.name,
//     materialCategory: item?.material?.category ?? "Unknown",
//     materialPoint: item?.material?.point ?? 0,
//     materialUnit: item?.material?.unit ?? "N/A",
//     materialId: item?.material?._id ?? "Unknown",
//     quantity: item?.quantity ?? 0,
//     reason: item?.reason ?? "No reason",
//     status:
//       item?.status === "A"
//         ? "Submitted"
//         : item?.status === "AD"
//         ? "Amended"
//         : item?.status === "P"
//         ? "Pending"
//         : item?.status === "R"
//         ? "Cancelled"
//         : "Unknown",
//     profId: item?.profile?._id ?? "N/A",
//     profileName: item?.profile?.name ?? "N/A",
//     dateCreated: item?.create_date
//       ? new Date(item.create_date).toLocaleString()
//       : "N/A",
//     dateUpdated: item?.last_update
//       ? new Date(item.last_update).toLocaleString()
//       : "N/A",
//     date: item?.date ? new Date(item.date).toLocaleString() : "N/A",
//   };
// };

const storeName = "transactions";
// Modify your createItemStore usage:
const useItems = createItemStore(
  storeName,
  (transaction) => transaction.transactionId,
  async (dateParams) => {
    // Accept single parameter object
    try {
      const { startDate, endDate } = dateParams;
      const params = {};

      if (startDate instanceof Date) {
        params.startDate = format(startDate, "yyyy-MM-dd");
      }
      if (endDate instanceof Date) {
        params.endDate = format(endDate, "yyyy-MM-dd");
      }

      // console.log("Fetching with params:", params);

      return await transactionService.getTransactions(
        null,
        null,
        params.startDate,
        params.endDate
      );
    } catch (error) {
      console.error("Error fetching transactions:", error);
      return [];
    }
  },
  transactionDataMap
);

// ========================================================

export default function Transaction() {
  const [isOpen, setIsOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [reload, setReload] = useState(false);
  const [loading, setLoading] = useState(false);
  const materials = useMaterialStore((state) => state.materials);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);

  // Add this to your component state
  const [dateRangeData, setDateRangeData] = useState([]);

  // Change the initial state to include formatted dates
  const [dateRange, setDateRange] = useState([
    {
      startDate: null,
      endDate: null,
      key: "selection",
    },
  ]);

  // New function to fetch data based on date range
  const fetchDateRangeData = async (startDate, endDate) => {
    try {
      setLoading(true);
      const formattedStart = startDate ? format(startDate, "yyyy-MM-dd") : null;
      const formattedEnd = endDate ? format(endDate, "yyyy-MM-dd") : null;

      const response = await transactionService.getTransactions(
        null,
        null,
        formattedStart,
        formattedEnd
      );

      setDateRangeData(response.Data || response || []);
    } catch (error) {
      console.error("Error fetching date range data:", error);
      setDateRangeData([]);
    } finally {
      setLoading(false);
    }
  };

  const [formData, setFormData] = useState({
    materialPoint: 0,
    transactionPoint: 0,
  });

  const actions = (row) => [
    {
      label: "View details",
      icon: <FaEye />,
      onClick: () => {
        setSelectedRow(row);
        setIsViewOpen(true);
      },
      variant: "dropPlain",
    },
    {
      label: "Edit details",
      icon: <FaPencilAlt />,
      onClick: () => {
        setSelectedRow(row);
        setIsOpen(true);
      },
    },
  ];

  const InfoRow = ({ label, value }) => (
    <div className="flex items-center">
      <div className="font-semibold w-1/2 text-left">{label}</div>
      <span className="text-left w-1/2">{value || "N/A"}</span>
    </div>
  );

  // Calculate transaction point whenever quantity or materialPoint changes
  useEffect(() => {
    if (selectedRow) {
      const quantity = Number(formData.quantity) || 0;
      const materialPoint = Number(formData.materialPoint) || 0;
      setFormData((prev) => ({
        ...prev,
        transactionPoint: quantity * materialPoint,
      }));
    }
  }, [formData.quantity, formData.materialPoint, selectedRow]);

  // Populate form when modal opens with selected row's data
  useEffect(() => {
    if (selectedRow) {
      const material = materials.find(
        (mat) => mat._id === selectedRow.materialId
      );
      setSelectedMaterial(material);

      setFormData({
        status:
          selectedRow.status === "Submitted"
            ? "A"
            : selectedRow.status === "Cancelled"
            ? "R"
            : selectedRow.status === "Amended"
            ? "AD"
            : selectedRow.status,
        profId: selectedRow.profId || "",
        profileName: selectedRow.profileName || "",
        pocId: selectedRow.pocId || "",
        pocName: selectedRow.pocName || "",
        materialId: selectedRow.materialId || "", // Make sure this is set
        materialCategory: selectedRow.materialCategory || "",
        materialPoint: material?.point || 0,
        quantity: selectedRow.quantity || 0,
        reason: selectedRow.reason || "No reason",
        transactionPoint: (material?.point || 0) * (selectedRow.quantity || 0),
      });
    }
  }, [selectedRow, isOpen, materials]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleMaterialChange = (material) => {
    setSelectedMaterial(material);
    handleChange("materialId", material?._id);
    handleChange("materialCategory", material?.category || "");
    handleChange("materialPoint", material?.point || 0);
  };

  const handleSubmit = async () => {
    if (!selectedRow) return;
    setLoading(true);

    const payload = {
      TransactionId: selectedRow.transactionId,
      ProfileId: formData.profId,
      PocId: formData.pocId,
      PocAttendantId: selectedRow.pocAttendantId || null,
      CollectorId: selectedRow.collectorId || null,
      MaterialId: formData.materialId,
      TransactionUnit: "kg",
      TransactionPoint: formData.transactionPoint,
      Quantity: Number(formData.quantity),
      Reason: formData.reason,
      Status:
        formData.status === "Submitted"
          ? "A"
          : formData.status === "Cancelled"
          ? "R"
          : formData.status,
    };

    try {
      await transactionService.editTransaction(payload);
      toast.success("🎉 Transaction updated successfully!", {
        autoClose: 2000,
      });
      fetchItems();
      setReload((prev) => !prev);
      setIsOpen(false);
    } catch (error) {
      console.error("Error submitting transaction:", error);
      toast.error("❌ Failed to update Transaction", {
        autoClose: 2000,
      });
    } finally {
      setLoading(false);
      setFormData({});
    }
  };

  // ========================================================
  // Step 2: Zustand store and page hydration
  // ========================================================
  const { isLoading, fetchItems, getSortedFiltered } = useItems(
    (state) => state
  );

  const items = useItems((state) => state.items);

  const displayData = useMemo(() => {
    // If we have date range data, use that (already mapped in fetchDateRangeData)
    if (dateRangeData.length > 0) {
      return dateRangeData.map(transactionDataMap);
    }

    // Otherwise use the regular items, ensuring they're properly mapped
    return Object.values(items).map((item) => {
      // If items are already mapped, return them as-is
      if (item.transactionId) return item;
      // Otherwise apply the mapping function
      return transactionDataMap(item);
    });
  }, [dateRangeData, items]);

  const showLoading = displayData.length === 0 && isLoading;
  //  console.log(`${storeName}:`, data);
  useEffect(() => {
    // Initial data load with no filters
    fetchItems().catch((error) => {
      console.error("Initial data load failed:", error);
    });
  }, []);
  // ========================================================

  const applyFilters = async () => {
    try {
      const { startDate, endDate } = dateRange[0];

      if (!startDate || !endDate) {
        throw new Error("Please select both dates");
      }

      await fetchDateRangeData(startDate, endDate);
      setFilterOpen(false);
    } catch (error) {
      console.error("Filter error:", error);
      alert(error.message || "Failed to apply filters");
    }
  };

  const clearFilters = () => {
    setDateRange([
      {
        startDate: null,
        endDate: null,
        key: "selection",
      },
    ]);
    setDateRangeData([]);
    setFilterOpen(false);
  };

  return (
    <div className="flex flex-col md:flex-row min-h-screen">
      <div className="flex-1 flex flex-col">
        <div className="p-8 flex flex-col">
          <div className="bg-white shadow rounded-lg md:max-w-[calc(100vw-375px)] p-4 sm:p-6 text-sm md:mt-0">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-4">
              <div className="text-base sm:text-lg font-semibold text-[#111827] mb-4 sm:mb-0">
                Transaction List
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setFilterOpen(true)}
                  className="flex items-center gap-2"
                >
                  <FaFilter />
                  Filter
                </Button>
                <Button
                  variant="outline"
                  onClick={() => exportToCsv(displayData, "transactions.csv")}
                  className="flex items-center gap-2"
                >
                  <FaDownload />
                  Export to CSV
                </Button>
              </div>
            </div>
            <hr />
            <div className="overflow-x-auto w-full md:max-w-[calc(100vw-375px)]">
              {showLoading ? (
                <LoadingImage />
              ) : displayData.length > 0 ? (
                <DataTable
                  columns={columns}
                  data={displayData.slice().reverse()}
                  actions={actions}
                />
              ) : (
                <div className="text-center text-gray-500 py-6">
                  {loading ? "Loading..." : "No requests found."}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Filter Modal */}
        <Modal
          isOpen={filterOpen}
          onClose={() => setFilterOpen(false)}
          title="Filter Transactions"
          description="Select a date range to filter transactions"
          actions={
            <div className="flex justify-between w-full">
              <div>
                <Button variant="ghost" onClick={clearFilters}>
                  Clear Filters
                </Button>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  onClick={() => setFilterOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={applyFilters}
                  className="bg-[#14B8A6] hover:bg-[#0D9488]"
                >
                  Apply Filters
                </Button>
              </div>
            </div>
          }
        >
          <div className="space-y-4">
            <div>
              <Label>Date Range:</Label>
              <DateRange
                editableDateInputs={true}
                // Update the date range handler to maintain parameter order
                onChange={(item) => {
                  const { startDate, endDate } = item.selection;
                  setDateRange([
                    {
                      startDate: startDate,
                      endDate: endDate,
                      key: "selection",
                    },
                  ]);
                }}
                moveRangeOnFirstSelection={false}
                ranges={dateRange}
                className="date-range-wrapper"
                rangeColors={["#14B8A6"]}
                minDate={new Date(2020, 0, 1)} // Optional: Set minimum selectable date
              />
              <div className="mt-2 text-sm text-gray-600">
                <span className="font-medium">Selected Range:</span>{" "}
                {dateRange[0].startDate
                  ? format(dateRange[0].startDate, "MMM d, yyyy")
                  : "Not selected"}{" "}
                to{" "}
                {dateRange[0].endDate
                  ? format(dateRange[0].endDate, "MMM d, yyyy")
                  : "Not selected"}
              </div>
            </div>
          </div>
        </Modal>

        {/* View Modal */}
        <Modal
          isOpen={isViewOpen}
          onClose={() => setIsViewOpen(false)}
          title="View Transaction Details"
          description="Here are the details for this transaction."
          actions={
            <div className="flex justify-between w-full">
              <Button
                variant={"secondary"}
                onClick={() => setIsViewOpen(false)}
              >
                Close
              </Button>
            </div>
          }
        >
          <div className="max-h-[70vh] overflow-y-auto space-y-2 px-4">
            {selectedRow ? (
              <div className="grid gap-2">
                <div className="bg-green-100 p-4 rounded-lg mb-4 grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm font-semibold text-gray-700">
                      Transaction ID
                    </p>
                    <p className="text-base text-gray-900 break-words whitespace-normal">
                      <ShortenedText text={selectedRow.transactionId} />
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-700">
                      Date Created
                    </p>
                    <p className="text-base text-gray-900 break-words whitespace-normal">
                      {selectedRow.dateCreated}
                    </p>
                  </div>
                  <div className="col-span-2 md:col-span-1">
                    <p className="text-sm font-semibold text-gray-700">
                      Status
                    </p>
                    <p className="text-base text-gray-900 break-words whitespace-normal">
                      {selectedRow.status}
                    </p>
                  </div>
                </div>
                {[
                  { label: "Profile ID:", value: selectedRow.profileId },
                  { label: "Profile Name:", value: selectedRow.profileName },
                  {
                    label: "POC ID:",
                    value: <ShortenedText text={selectedRow.pocId} />,
                  },
                  { label: "POC Name:", value: selectedRow.pocName },
                  {
                    label: "Material Category:",
                    value: `${selectedRow.materialCategory} (${selectedRow.materialUnit})`,
                  },
                  { label: "Quantity:", value: selectedRow.quantity },
                  { label: "Reason:", value: selectedRow.reason },
                ].map((item, index) => (
                  <div
                    key={item.label}
                    className={`px-4 py-2 rounded-lg ${
                      index % 2 === 0 ? "bg-gray-50" : "bg-white"
                    }`}
                  >
                    <InfoRow label={item.label} value={item.value} />
                  </div>
                ))}
              </div>
            ) : (
              <p>Loading...</p>
            )}
          </div>
        </Modal>

        {/* Edit Transaction Modal */}
        <Modal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          title="Edit Transaction"
          description="Modify the details for this transaction below."
          actions={
            <div className="flex justify-between w-full">
              <Button variant="secondary" onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
              <Button disabled={loading} onClick={handleSubmit}>
                {loading ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          }
        >
          <div className="max-h-[70vh] overflow-y-auto space-y-6 ">
            {selectedRow ? (
              <>
                {/* Static Information Row */}
                <div className="bg-green-100 p-4 rounded-lg mb-4 grid grid-cols-2 md:grid-cols-3 gap-4">
                  {/* Transaction ID */}
                  <div>
                    <p className="text-sm font-semibold text-gray-700">
                      Transaction ID
                    </p>
                    <p className="text-base text-gray-900 break-words whitespace-normal">
                      <ShortenedText text={selectedRow.transactionId} />
                    </p>
                  </div>

                  {/* Date Created */}
                  <div>
                    <p className="text-sm font-semibold text-gray-700">
                      Date Created
                    </p>
                    <p className="text-base text-gray-900 break-words whitespace-normal">
                      {selectedRow.dateCreated}
                    </p>
                  </div>

                  {/* Status */}
                  <div className="col-span-2 md:col-span-1">
                    <p className="text-sm font-semibold text-gray-700">
                      Status
                    </p>
                    <p className="text-base text-gray-900 break-words whitespace-normal">
                      {selectedRow.status}
                    </p>
                  </div>
                </div>

                <div className="border-t border-gray-300" />

                {/* Editable Fields */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Status */}
                  <div className="col-span-1 sm:col-span-2">
                    <Label>Status:</Label>
                    <Select
                      value={formData.status}
                      onValueChange={(value) => handleChange("status", value)}
                    >
                      <SelectTrigger className="w-full border bg-white border-gray-300 p-2 rounded-lg">
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="A">Submitted</SelectItem>
                        <SelectItem value="AD">Amended</SelectItem>
                        <SelectItem value="R">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Display rows with alternating backgrounds */}
                  <div className="col-span-1 sm:col-span-2">
                    <div className="flex justify-between items-center py-3 bg-gray-50">
                      <span className="font-medium text-gray-700">
                        Profile ID:
                      </span>
                      <span className="text-gray-900">{formData.profId}</span>
                    </div>
                  </div>

                  <div className="col-span-1 sm:col-span-2">
                    <div className="flex justify-between items-center py-3 bg-white">
                      <span className="font-medium text-gray-700">
                        Profile Name:
                      </span>
                      <span className="text-gray-900">
                        {formData.profileName}
                      </span>
                    </div>
                  </div>

                  <div className="col-span-1 sm:col-span-2">
                    <div className="flex justify-between items-center py-3 bg-gray-50">
                      <span className="font-medium text-gray-700">POC ID:</span>
                      <span className="text-gray-900">{formData.pocId}</span>
                    </div>
                  </div>

                  <div className="col-span-1 sm:col-span-2">
                    <div className="flex justify-between items-center py-3 bg-white">
                      <span className="font-medium text-gray-700">
                        POC Name:
                      </span>
                      <span className="text-gray-900">{formData.pocName}</span>
                    </div>
                  </div>

                  {/* Material Select */}
                  <div className="sm:col-span-2">
                    <Label>Material Category:</Label>
                    <MaterialSelectTransactions
                      value={formData.materialId} // Pass the material ID
                      onChange={handleMaterialChange}
                    />
                  </div>

                  <div>
                    <Label>Quantity:</Label>
                    <Input
                      type="number"
                      value={formData.quantity}
                      onChange={(e) => handleChange("quantity", e.target.value)}
                    />
                  </div>

                  <div className="col-span-1 sm:col-span-2">
                    <div className="flex justify-between items-center py-3 bg-gray-50">
                      <span className="font-medium text-gray-700">
                        Calculated Points:
                      </span>
                      <span className="text-gray-900">
                        {formData.transactionPoint}
                      </span>
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <Label>Reason:</Label>
                    <TextArea
                      rows={4}
                      placeholder="Enter reason for the change..."
                      value={formData.reason}
                      onChange={(e) => handleChange("reason", e.target.value)}
                    />
                  </div>
                </div>
              </>
            ) : (
              <p>Loading...</p>
            )}
          </div>
        </Modal>
      </div>
    </div>
  );
}
